/**
 * Message send / list / delete using the unified `conversations/{convId}`
 * schema. The legacy three-path fan-out (notifications + chatMessages +
 * messagesBySender) is gone — every message lives in one canonical place.
 *
 * Schema:
 *   conversations/{convId}/
 *     metadata/
 *       participants/{uid}: { plate, lastReadAt }
 *       lastMessage:        { from, body, sentAt }
 *       createdAt, updatedAt
 *       lastSmsAt           (for client-side rate-limiting display)
 *     messages/{msgId}:     { from, body, sentAt, smsSent }
 *
 *   userConversations/{uid}/{convId}:
 *     peerPlate, peerUid, lastMessage, unreadCount, updatedAt
 *
 * `convId` is `[plateA, plateB].sort().join('_')` so both participants
 * compute the same key without needing to know each other's UIDs.
 */

import { auth, db, ref } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { normalize as normalizePhone } from '../../shared/phone.js';
import { createLogger } from '../../core/logger.js';
import * as sms from './sms.service.js';

const log = createLogger('message');

/**
 * @typedef {Object} SendInput
 * @property {string} toPlate
 * @property {string} body
 * @property {boolean} [sharePhone]
 * @property {boolean} [sendSms]
 * @property {string} [presetId]
 *
 * @typedef {Object} SendResult
 * @property {'ok' | 'rate-limited' | 'no-recipient' | 'no-auth' | 'invalid' | 'error'} status
 * @property {string} [msgId]
 * @property {string} [convId]
 * @property {string} [smsError]
 * @property {string} [message]
 */

const RATE_WINDOW_MS = 60 * 60 * 1000;

/**
 * Build the deterministic conversation key from two plates.
 * @param {string} a
 * @param {string} b
 * @returns {string}
 */
export function conversationKey(a, b) {
  return [a || '_', b || '_'].sort().join('_');
}

/**
 * Send a message to the owner of the given plate.
 * @param {SendInput} input
 * @returns {Promise<SendResult>}
 */
export async function send(input) {
  const fromUid = auth().currentUser?.uid;
  if (!fromUid) return { status: 'no-auth', message: 'נדרשת התחברות.' };
  const fromPlate = normalizePlate(LocalState.plate ?? '');
  const fromPhone = normalizePhone(LocalState.phone ?? '');

  const toPlate = normalizePlate(input.toPlate);
  if (!toPlate) return { status: 'invalid', message: 'מספר רכב לא תקין.' };
  const body = String(input.body ?? '').trim();
  if (!body) return { status: 'invalid', message: 'ההודעה ריקה.' };
  if (!fromPlate) return { status: 'invalid', message: 'חסר מספר רכב לחשבון שלך.' };

  const toUid = /** @type {string | null} */ ((await ref(`plateIndex/${toPlate}`).once('value')).val());
  if (!toUid) return { status: 'no-recipient', message: 'הרכב לא רשום במערכת.' };
  if (toUid === fromUid) return { status: 'invalid', message: 'אין אפשרות לשלוח הודעה לעצמך.' };

  const convId = conversationKey(fromPlate, toPlate);
  const now = Date.now();

  // The rate-limit gates the *SMS dispatch* only — not the in-app message.
  // Once two users have an active conversation, they can chat freely; we only
  // throttle the actual SMS to the driver's phone (which costs money and is
  // disruptive to the recipient). The first message in any conversation
  // always fires the SMS (since no prior SMS timestamp exists).
  let lastSmsAt = null;
  try {
    lastSmsAt = (await ref(`conversations/${convId}/metadata/lastSmsAt`).once('value')).val();
  } catch (err) {
    log.warn('lastSmsAt read failed, treating as no prior SMS:', err);
  }
  const smsAllowedByRate =
    typeof lastSmsAt !== 'number' || now - lastSmsAt >= RATE_WINDOW_MS;

  // Recipient phone (only for SMS).
  /** @type {any} */
  const toVehicle = (await ref(`vehicles/${toPlate}`).once('value')).val() ?? {};
  const toPhone = normalizePhone(toVehicle?.phone ?? '');

  const msgId = ref(`conversations/${convId}/messages`).push().key;
  if (!msgId) return { status: 'error', message: 'שגיאה ביצירת מזהה הודעה.' };

  const message = {
    id: msgId,
    from: fromUid,
    body,
    sentAt: now,
    smsSent: false,
    presetId: input.presetId ?? null,
  };
  const lastMessage = { from: fromUid, body, sentAt: now };

  // Decide whether this message will actually trigger an SMS to the driver.
  // SMS fires only when: (1) the caller didn't explicitly opt out, (2) we have
  // a phone number to send to, AND (3) the per-hour rate-limit allows it.
  const willSendSms = input.sendSms !== false && !!toPhone && smsAllowedByRate;

  // Fan-out: message + metadata + both user indexes. `lastSmsAt` is only
  // bumped when we'll actually dispatch an SMS — in-app-only messages don't
  // count toward the hourly limit.
  /** @type {Record<string, any>} */
  const updates = {
    [`conversations/${convId}/messages/${msgId}`]: message,
    [`conversations/${convId}/metadata/lastMessage`]: lastMessage,
    [`conversations/${convId}/metadata/updatedAt`]: now,
    [`conversations/${convId}/metadata/participants/${fromUid}/plate`]: fromPlate,
    [`conversations/${convId}/metadata/participants/${fromUid}/lastReadAt`]: now,
    [`conversations/${convId}/metadata/participants/${toUid}/plate`]: toPlate,
  };
  if (willSendSms) {
    updates[`conversations/${convId}/metadata/lastSmsAt`] = now;
  }
  Object.assign(updates, {
    [`userConversations/${fromUid}/${convId}/peerPlate`]: toPlate,
    [`userConversations/${fromUid}/${convId}/peerUid`]: toUid,
    [`userConversations/${fromUid}/${convId}/lastMessage`]: lastMessage,
    [`userConversations/${fromUid}/${convId}/mineLast`]: true,
    [`userConversations/${fromUid}/${convId}/updatedAt`]: now,
    [`userConversations/${toUid}/${convId}/peerPlate`]: fromPlate,
    [`userConversations/${toUid}/${convId}/peerUid`]: fromUid,
    [`userConversations/${toUid}/${convId}/lastMessage`]: lastMessage,
    [`userConversations/${toUid}/${convId}/mineLast`]: false,
    [`userConversations/${toUid}/${convId}/updatedAt`]: now,
  });

  // Initialise `createdAt` only on first message. Wrap in try/catch for the
  // same reason as lastSmsAt — for a brand-new conversation the parent read
  // rule denies non-participants, but the targeted child rule allows it.
  try {
    const metaSnap = await ref(`conversations/${convId}/metadata/createdAt`).once('value');
    if (!metaSnap.exists()) updates[`conversations/${convId}/metadata/createdAt`] = now;
  } catch (err) {
    // Read denied — assume new conversation and set createdAt anyway. The
    // write rule still verifies participant membership.
    updates[`conversations/${convId}/metadata/createdAt`] = now;
    log.warn('createdAt read failed, setting unconditionally:', err);
  }

  // Increment unread for recipient via transaction so concurrent sends don't drop a count.
  try {
    await db().ref().update(updates);
    await ref(`userConversations/${toUid}/${convId}/unreadCount`).transaction(
      (/** @type {number | null} */ cur) => (cur || 0) + 1,
    );
  } catch (err) {
    log.error('fan-out write failed:', err);
    return { status: 'error', message: 'שגיאה בשליחת ההודעה.' };
  }

  // Best-effort SMS — only when the rate-limit allows. In-app message above
  // is already saved regardless, so the conversation flows freely.
  // The SMS template is built inside sms.service from (body, senderPhone,
  // sharePhone) so we just pass the raw inputs through.
  /** @type {string | undefined} */
  let smsError;
  if (willSendSms) {
    const result = await sms.send({
      to: toPhone,
      body,
      senderPhone: fromPhone,
      sharePhone: input.sharePhone !== false,
    });
    if (result.ok) {
      await ref(`conversations/${convId}/messages/${msgId}/smsSent`).set(true);
    } else {
      smsError = result.error;
      log.warn('SMS dispatch failed:', smsError);
      // Revert `lastSmsAt` to its previous value so the hourly rate-limit
      // doesn't block the user from retrying. Without this, a failed SMS
      // locks the conversation out of SMS dispatch for the next hour even
      // though no SMS was actually delivered.
      try {
        await ref(`conversations/${convId}/metadata/lastSmsAt`)
          .set(typeof lastSmsAt === 'number' ? lastSmsAt : null);
      } catch (e) {
        log.warn('lastSmsAt revert failed:', e);
      }
    }
  }

  return { status: 'ok', msgId, convId, smsError };
}

/**
 * Mark every received message in a conversation as read by the current user.
 * Clears `unreadCount` and stamps `lastReadAt`.
 * @param {string} convId
 */
export async function markConversationRead(convId) {
  const uid = auth().currentUser?.uid;
  if (!uid || !convId) return;
  const now = Date.now();
  try {
    await db().ref().update({
      [`userConversations/${uid}/${convId}/unreadCount`]: 0,
      [`conversations/${convId}/metadata/participants/${uid}/lastReadAt`]: now,
    });
  } catch (err) { log.warn('markConversationRead failed:', err); }
}

/**
 * Sent-message history for the current user. Reads from `userConversations`
 * + the full message list of each conversation, filtering to messages where
 * `from === uid`.
 *
 * @returns {Promise<any[]>}
 */
export async function listSent() {
  const uid = auth().currentUser?.uid;
  if (!uid) return [];
  const convsSnap = await ref(`userConversations/${uid}`)
    .orderByChild('updatedAt')
    .limitToLast(50)
    .once('value');
  /** @type {string[]} */
  const convIds = [];
  convsSnap.forEach((/** @type {any} */ c) => { if (c.key) convIds.push(c.key); });

  /** @type {any[]} */
  const rows = [];
  for (const convId of convIds) {
    const messagesSnap = await ref(`conversations/${convId}/messages`)
      .orderByChild('sentAt')
      .limitToLast(100)
      .once('value');
    /** @type {any} */
    const peer = convsSnap.child(convId).val() ?? {};
    messagesSnap.forEach((/** @type {any} */ m) => {
      const v = m.val();
      if (v && v.from === uid) {
        rows.push({
          ...v,
          id: m.key,
          convId,
          toPlate: peer.peerPlate ?? '',
          to: peer.peerUid ?? '',
        });
      }
    });
  }
  return rows.sort((a, b) => (b.sentAt || 0) - (a.sentAt || 0));
}

/**
 * Delete one message. Removes from `conversations/{convId}/messages` only
 * — the userConversations index stays so the conversation thread is still
 * navigable.
 *
 * @param {{ msgId: string; convId: string }} args
 */
export async function deleteSent({ msgId, convId }) {
  const uid = auth().currentUser?.uid;
  if (!uid || !msgId || !convId) return;
  try { await ref(`conversations/${convId}/messages/${msgId}`).remove(); }
  catch (err) { log.warn('deleteSent failed:', err); }
}

/** Delete every message the current user sent. Conversations are kept. */
export async function deleteAllSent() {
  const rows = await listSent();
  if (rows.length === 0) return;
  /** @type {Record<string, null>} */
  const updates = {};
  for (const row of rows) {
    if (row.convId && row.id) {
      updates[`conversations/${row.convId}/messages/${row.id}`] = null;
    }
  }
  if (Object.keys(updates).length === 0) return;
  try { await db().ref().update(updates); }
  catch (err) { log.warn('deleteAllSent failed:', err); }
}
